/**
 * CAD 会话状态管理
 * 管理 OpenProject 返回的会话信息、项目文件列表与文件下载状态
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { CadArchiveFile, FileInfo, OpenProjectResponse, CadNode } from '@puppycad/proto'
import { Code, ConnectError } from '@connectrpc/connect'
import {
  openProjectSession,
  downloadCadFile,
  createOpenProjectDownloadContextId,
  registerOpenProjectDownloadContext,
  unregisterOpenProjectDownloadContext,
  type OpenProjectDownloadFileContext,
} from '@/services/api/cad-session'
import { ApiErrnoError } from '@/services/api/ApiErrnoError'
import { resolveError } from '@/services/api/error-handler'
import {
  normalizeOpenProjectSessionSource,
  type OpenProjectSessionInput,
  type OpenProjectSessionSource,
} from '@/services/api/open-project-input'
import {
  CadArchiveFileSnapshotMapper,
  type PendingInsertedNodeValidationRecord,
} from '@/services/project-model-loading'
import {
  filterGlbFiles,
  findPrimaryGlbFile,
  findPrimaryCadFile,
  normalizeImportedAssetFileId,
} from '@puppycad/cad-core'

const OPEN_PROJECT_RUNTIME_UUID_PREFIX = 'open-project:'
const OPEN_PROJECT_NODE_RUNTIME_UUID_PREFIX = 'open-project-node:'

/**
 * OpenProject 根节点的标题展示上下文。
 *
 * 设计说明：项目身份来自 `OpenProjectResponse.projectId/projectName`；
 * 节点身份来自当前打开仓库节点的 `instance_name/branch_name`，避免 UI 继续展示 `_assembly.json`。
 */
export interface OpenProjectRootDisplayContext {
  /** 当前打开项目 ID。 */
  readonly projectId: string
  /** 当前打开项目名称，来源于 `OpenProjectResponse.projectName`。 */
  readonly projectName: string
  /** 当前打开节点名称，来源于 `CadNode.instanceName`。 */
  readonly nodeName: string
  /** 当前打开分支名称，来源于 `CadNode.branchName`。 */
  readonly branchName: string
}

/**
 * CAD 会话初始化的生命周期选项。
 */
export interface CadSessionInitOptions {
  /** 调用方控制本次 OpenProject 会话初始化生命周期的取消信号。 */
  readonly signal?: AbortSignal
}

/**
 * 解析 OpenProject GLB 在前端缓存链路使用的运行时 uuid。
 *
 * 设计说明：OpenProject 不再保证普通 GLB 返回业务 uuid，但现有 tab/cache/registry
 * 仍以非空 uuid 作为身份 key；这里仅为 `.glb` 补运行时 key，文件下载仍由底层 API
 * 统一按 fileId/path 读取，避免把运行时 key 当作后端业务 uuid 使用。
 *
 * @param file OpenProject 返回的文件描述。
 * @returns 可供前端身份链路使用的 uuid。
 */
function resolveOpenProjectRuntimeUuid(file: FileInfo): string {
  const fileUuid = typeof file.uuid === 'string' ? file.uuid.trim() : ''
  if (fileUuid) {
    return fileUuid
  }

  const normalizedFileId = normalizeImportedAssetFileId(file.fileId)
  if (file.kind === '.glb' && normalizedFileId) {
    return `${OPEN_PROJECT_RUNTIME_UUID_PREFIX}${normalizedFileId}`
  }

  return file.uuid ?? ''
}

/**
 * 归一化 OpenProject 文件列表。
 *
 * @param files 后端返回的项目文件列表。
 * @returns 仅补齐 GLB 运行时 uuid 后的文件列表。
 */
function normalizeOpenProjectProjectFiles(files: readonly FileInfo[]): FileInfo[] {
  return files.map((file) => {
    const runtimeUuid = resolveOpenProjectRuntimeUuid(file)
    return runtimeUuid === file.uuid ? file : ({ ...file, uuid: runtimeUuid } as FileInfo)
  })
}

interface OpenProjectModelLoadFilesContext {
  /** 根仓库文件清单，继续供 tab/UI 使用。 */
  readonly projectFiles: FileInfo[]
  /** 根仓库和子模块仓库递归展开后的文件清单，仅供 loader 依赖解析使用。 */
  readonly modelLoadFiles: FileInfo[]
  /** 每个文件所属的真实仓库提交上下文。 */
  readonly downloadFileContexts: OpenProjectDownloadFileContext[]
  /** 响应级总归档包；不存在时保持 null。 */
  readonly archiveFile: PuppyCADWeb.CadArchiveFileSnapshot | null
}

/**
 * 解析子模块节点 GLB 在前端缓存链路使用的运行时 uuid。
 *
 * 设计说明：子模块仓库可能与根仓库存在同名 GLB，运行时 uuid 必须包含仓库和提交，
 * 才能避免 loader 缓存把不同节点误认为同一资产；真实下载仍按 fileId 和下载上下文完成。
 *
 * @param node 当前 OpenProject 节点。
 * @param file 当前节点返回的文件描述。
 * @returns 可供前端缓存身份使用的 uuid。
 */
function resolveOpenProjectNodeRuntimeUuid(node: CadNode, file: FileInfo): string {
  const fileUuid = typeof file.uuid === 'string' ? file.uuid.trim() : ''
  if (fileUuid) {
    return fileUuid
  }

  const normalizedFileId = normalizeImportedAssetFileId(file.fileId)
  if (file.kind === '.glb' && normalizedFileId) {
    const commitId = node.commitId.trim() || 'no-commit'
    return `${OPEN_PROJECT_NODE_RUNTIME_UUID_PREFIX}${node.repositoryId.toString()}:${commitId}:${normalizedFileId}`
  }

  return file.uuid ?? ''
}

/**
 * 归一化某个 OpenProject 节点的文件清单。
 *
 * @param node 当前 OpenProject 节点。
 * @param isRoot 是否为根节点；根节点沿用旧运行时 uuid 前缀以保持缓存兼容。
 * @returns 可进入前端加载链路的文件清单。
 */
function normalizeOpenProjectNodeFiles(node: CadNode, isRoot: boolean): FileInfo[] {
  if (isRoot) {
    return normalizeOpenProjectProjectFiles(node.files)
  }

  return node.files.map((file) => {
    const runtimeUuid = resolveOpenProjectNodeRuntimeUuid(node, file)
    return runtimeUuid === file.uuid ? file : ({ ...file, uuid: runtimeUuid } as FileInfo)
  })
}

/**
 * 生成单个文件的真实下载上下文。
 *
 * @param projectId OpenProject 响应中的项目 ID。
 * @param node 文件所属 OpenProject 节点。
 * @param file 已归一化的文件描述。
 * @returns 可注册到下载入口的文件上下文。
 */
function createOpenProjectDownloadFileContext(
  projectId: bigint,
  node: CadNode,
  file: FileInfo,
): OpenProjectDownloadFileContext {
  return {
    projectId,
    repositoryId: node.repositoryId,
    branchId: node.branchId,
    commitId: node.commitId,
    fileId: file.fileId,
    fileUuid: file.uuid,
  }
}

/**
 * 从 OpenProject 节点树递归派生模型加载文件清单和下载上下文。
 *
 * 设计说明：`projectFiles` 仍只表示当前打开根仓库，避免子模块 GLB 被当作底部 tab；
 * `modelLoadFiles` 才是 loader 依赖解析用的完整候选集合。
 *
 * @param rootNode OpenProject 根节点。
 * @param projectId OpenProject 响应中的项目 ID。
 * @param archiveFile OpenProject 响应级总归档包。
 * @returns 根文件、loader 文件和文件级下载上下文。
 */
function collectOpenProjectModelLoadFiles(
  rootNode: CadNode | undefined,
  projectId: bigint,
  archiveFile?: CadArchiveFile,
): OpenProjectModelLoadFilesContext {
  if (!rootNode) {
    return {
      projectFiles: [],
      modelLoadFiles: [],
      downloadFileContexts: [],
      archiveFile: null,
    }
  }

  const modelLoadFiles: FileInfo[] = []
  const downloadFileContexts: OpenProjectDownloadFileContext[] = []
  let projectFiles: FileInfo[] = []

  function visitNode(node: CadNode, isRoot: boolean): void {
    const nodeFiles = normalizeOpenProjectNodeFiles(node, isRoot)
    if (isRoot) {
      projectFiles = nodeFiles
    }

    modelLoadFiles.push(...nodeFiles)
    nodeFiles.forEach((file) => {
      downloadFileContexts.push(createOpenProjectDownloadFileContext(projectId, node, file))
    })
    node.children.forEach((child) => visitNode(child, false))
  }

  visitNode(rootNode, true)

  let normalizedArchiveFile: PuppyCADWeb.CadArchiveFileSnapshot | null = null
  const archiveSnapshot = new CadArchiveFileSnapshotMapper().map(archiveFile, 'OpenProject')
  if (archiveSnapshot) {
    const archiveRuntimeUuid = resolveOpenProjectRuntimeUuid(archiveSnapshot.fileInfo)
    const normalizedCandidate = archiveRuntimeUuid === archiveSnapshot.fileInfo.uuid
      ? archiveSnapshot.fileInfo
      : ({ ...archiveSnapshot.fileInfo, uuid: archiveRuntimeUuid } as FileInfo)
    const normalizedFileInfo = modelLoadFiles.find((file) => (
      file.fileId === normalizedCandidate.fileId
      && file.uuid === normalizedCandidate.uuid
    )) ?? normalizedCandidate
    normalizedArchiveFile = {
      ...archiveSnapshot,
      fileInfo: normalizedFileInfo,
    }

    if (!modelLoadFiles.includes(normalizedFileInfo)) {
      modelLoadFiles.push(normalizedFileInfo)
    }
    // 下载上下文采用“最早注册优先”；归档可能同时出现在 project.files，必须前置其权威身份，
    // 否则同 UUID/路径会错误命中外层 CadNode 的仓库与提交。
    downloadFileContexts.unshift({
      projectId,
      repositoryId: archiveSnapshot.repositoryId,
      commitId: archiveSnapshot.commitId,
      fileId: normalizedFileInfo.fileId,
      fileUuid: normalizedFileInfo.uuid,
    })
  }

  return {
    projectFiles,
    modelLoadFiles,
    downloadFileContexts,
    archiveFile: normalizedArchiveFile,
  }
}

/**
 * 从 OpenProject 根节点派生编辑器顶部标题需要的轻量上下文。
 *
 * @param response OpenProject 返回的完整响应。
 * @param fallbackProjectId 路由中的项目 ID；当响应未携带有效 projectId 时作为兜底。
 * @returns 可供 UI 展示的根节点上下文；缺少根节点时返回 null。
 */
function createOpenProjectRootDisplayContext(
  response: OpenProjectResponse,
  fallbackProjectId: string,
): OpenProjectRootDisplayContext | null {
  const openedProject = response.project
  if (!openedProject) {
    return null
  }

  const responseProjectId = response.projectId.toString()
  const projectId = responseProjectId !== '0' ? responseProjectId : fallbackProjectId.trim()
  const branchIdText = openedProject.branchId.toString()
  const branchName = openedProject.branchName.trim()

  return {
    projectId,
    projectName: response.projectName.trim(),
    nodeName: openedProject.instanceName.trim() || '未命名节点',
    branchName: branchName || (branchIdText !== '0' ? branchIdText : '默认分支'),
  }
}

export const useCadSessionStore = defineStore('cad-session', () => {
  /**
   * 兼容现有 loader 入参命名的下载上下文 ID。
   *
   * 设计说明：最新 OpenProjectResponse 不再返回后端 sessionId，但 loader 仍统一通过
   * `downloadCadFile(...)` 读取模型字节；这里暂时复用 `sessionId` 这个 store 字段承载
   * 前端生成的 `downloadContextId`，避免本轮下载入口联调扩散到所有 loader 签名。
   */
  const sessionId = ref('')
  const currentVersion = ref('')
  const lastKnownVersion = ref('')
  const projectFiles = ref<FileInfo[]>([])
  const modelLoadFiles = ref<FileInfo[]>([])
  const actionRecords = ref<Record<string, unknown>>({})
  const currentRepositoryId = ref<bigint | null>(null)
  const currentBranchId = ref<bigint | null>(null)
  const currentCommitId = ref('')
  const openProjectSnapshotCommitId = ref('')
  const writeBaseCommitId = ref('')
  const requiresReload = ref(false)
  const pendingInsertedNodeValidations = ref<PendingInsertedNodeValidationRecord[]>([])
  const openProjectRootNode = ref<CadNode | null>(null)
  const openProjectArchiveFile = ref<PuppyCADWeb.CadArchiveFileSnapshot | null>(null)
  const openProjectRootDisplayContext = ref<OpenProjectRootDisplayContext | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  let initPromise: Promise<Uint8Array | null> | null = null
  let initPromiseKey = ''
  let initPromiseReusable = false
  let initRequestSequence = 0

  function createOpenProjectInitKey(input: OpenProjectSessionInput): string {
    return [
      input.projectId.toString(),
      input.repositoryId?.toString() ?? '',
      input.branchId?.toString() ?? '',
      input.commitId?.trim() ?? '',
    ].join('|')
  }

  function normalizeCadSessionError(err: unknown, fallback = '会话初始化失败'): string {
    if (err instanceof ConnectError) {
      if (err.code === Code.Unauthenticated) {
        return 'auth_expired: 登录已过期，请重试'
      }
      if (err.code === Code.Unavailable) {
        return 'network_unavailable: 网络不可用，请检查连接后重试'
      }
    }
    if (err instanceof ApiErrnoError) {
      return resolveError(err).message
    }
    if (err instanceof Error) {
      return err.message
    }
    return fallback
  }

  function isVersionChanged(previousVersion: string, nextVersion: string): boolean {
    if (!previousVersion && !nextVersion) return false
    if (!previousVersion || !nextVersion) return true
    return previousVersion !== nextVersion
  }

  /**
   * 注销当前 OpenProject 下载上下文。
   *
   * 设计说明：OpenProject 已改为 unary 快照读取，store 不再维护长连接；
   * 重新打开项目时只需要释放旧下载上下文，避免旧 sessionId 继续命中文件下载。
   */
  function unregisterCurrentDownloadContext(): void {
    if (sessionId.value) {
      unregisterOpenProjectDownloadContext(sessionId.value)
      sessionId.value = ''
    }
  }

  /** 获取所有可加载的 .glb 零件文件 */
  function getGlbFiles(): FileInfo[] {
    return filterGlbFiles(projectFiles.value)
  }

  /** 获取主 .glb 文件（体积最大的，通常是完整装配体） */
  function getPrimaryGlbFile(): FileInfo | undefined {
    return findPrimaryGlbFile(projectFiles.value)
  }

  /**
   * 判断会话初始化请求是否仍然拥有写入 store 的资格。
   *
   * 设计说明：reset 或后续 initSession 会递增序号，旧请求即使晚返回也只能退出，
   * 不能继续覆盖当前项目、下载上下文或加载状态。
   *
   * @param requestId 本次 initSession 开始时取得的请求序号。
   * @returns 请求是否仍然是当前最新请求。
   */
  function isActiveInitSessionRequest(requestId: number): boolean {
    return initRequestSequence === requestId
  }

  /**
   * 判断会话初始化异常是否属于调用方主动取消。
   *
   * @param err 捕获到的异常。
   * @param signal 本次初始化携带的取消信号。
   * @returns 是否应该静默结束而不写入错误状态。
   */
  function isCadSessionInitCanceled(err: unknown, signal: AbortSignal | undefined): boolean {
    return Boolean(signal?.aborted)
      || (err instanceof ConnectError && err.code === Code.Canceled)
  }

  /**
   * 初始化 CAD 会话
   * 步骤 1：OpenProject 获取项目根节点、下载上下文与 project_files
   * 步骤 2：解析项目文件列表，判断加载模式
   *   - 若存在 .glb 零件文件 → 由调用方批量加载（不在此处下载）
   *   - 若存在主 CAD 文件（kind=cad_part/cad_assembly 且有 uuid）→ 下载原始数据
   * @returns 主 CAD 文件字节数据，批量 GLB 模式或无目标文件时返回 null
   */
  async function initSession(
    projectId: OpenProjectSessionSource,
    options: CadSessionInitOptions = {},
  ): Promise<Uint8Array | null> {
    const signal = options.signal
    const openProjectInput = normalizeOpenProjectSessionSource(projectId)
    const initKey = createOpenProjectInitKey(openProjectInput)
    if (signal?.aborted) {
      return null
    }
    if (!signal && initPromiseReusable && initPromise && initPromiseKey === initKey) {
      return await initPromise
    }
    const initRequestId = initRequestSequence + 1
    initRequestSequence = initRequestId
    initPromiseKey = initKey
    initPromiseReusable = !signal
    initPromise = (async () => {
      isLoading.value = true
      error.value = null
      try {
        unregisterCurrentDownloadContext()
        const resp = await openProjectSession(openProjectInput, { signal })
        if (!isActiveInitSessionRequest(initRequestId) || signal?.aborted) {
          return null
        }
        const openedProject = resp.project
        const openProjectFilesContext = collectOpenProjectModelLoadFiles(
          openedProject,
          resp.projectId,
          resp.archiveFile,
        )
        const downloadContext = openedProject
          ? {
            projectId: resp.projectId,
            repositoryId: openedProject.repositoryId,
            branchId: openedProject.branchId,
            commitId: openedProject.commitId,
            files: openProjectFilesContext.downloadFileContexts,
          }
          : null
        const downloadContextId = downloadContext
          ? createOpenProjectDownloadContextId(downloadContext)
          : ''
        const normalizedProjectFiles = openProjectFilesContext.projectFiles
        const normalizedModelLoadFiles = openProjectFilesContext.modelLoadFiles
        const rootDisplayContext = createOpenProjectRootDisplayContext(
          resp,
          openProjectInput.projectIdText,
        )
        if (!isActiveInitSessionRequest(initRequestId) || signal?.aborted) {
          return null
        }
        sessionId.value = downloadContextId
        projectFiles.value = normalizedProjectFiles
        modelLoadFiles.value = normalizedModelLoadFiles
        actionRecords.value = {}
        currentRepositoryId.value = openedProject?.repositoryId ?? null
        currentBranchId.value = openedProject?.branchId ?? null
        const openedCommitId = openedProject?.commitId ?? ''
        currentCommitId.value = openedCommitId
        openProjectSnapshotCommitId.value = openedCommitId
        writeBaseCommitId.value = openedCommitId
        requiresReload.value = false
        // 待确认插入校验需要跨 OpenProject 重载保留，校验完成后由调用方显式清空。
        openProjectRootNode.value = openedProject ?? null
        openProjectArchiveFile.value = openProjectFilesContext.archiveFile
        // 会话版本标记统一使用 OpenProject 根节点 HEAD commit，避免 UI 依赖已移除的事件流字段。
        currentVersion.value = openedCommitId
        lastKnownVersion.value = openedCommitId
        openProjectRootDisplayContext.value = rootDisplayContext
        if (downloadContextId && downloadContext) {
          registerOpenProjectDownloadContext(downloadContextId, downloadContext)
        }

        const glbFiles = filterGlbFiles(normalizedProjectFiles)
        if (glbFiles.length > 0) {
          return null
        }

        // OpenProject 已返回完整节点树时，模型加载统一交给项目模型 loader。
        // 这里不能把 `_assembly.json` 或节点包文件再兜底当作旧 CAD 主文件下载。
        if (openedProject) {
          return null
        }

        const cadFile = findPrimaryCadFile(normalizedProjectFiles)
        if (!cadFile) return null

        if (!isActiveInitSessionRequest(initRequestId) || signal?.aborted) {
          return null
        }
        const cadBytes = await downloadCadFile(downloadContextId, cadFile.fileId, cadFile.uuid)
        return isActiveInitSessionRequest(initRequestId) && !signal?.aborted ? cadBytes : null
      } catch (e) {
        if (!isActiveInitSessionRequest(initRequestId) || isCadSessionInitCanceled(e, signal)) {
          return null
        }
        if (isActiveInitSessionRequest(initRequestId)) {
          error.value = normalizeCadSessionError(e)
        }
        return null
      } finally {
        if (isActiveInitSessionRequest(initRequestId)) {
          isLoading.value = false
          initPromise = null
          initPromiseKey = ''
          initPromiseReusable = false
        }
      }
    })()
    return await initPromise
  }

  /** 清空会话状态（页面离开时调用） */
  function reset() {
    initRequestSequence += 1
    initPromise = null
    initPromiseKey = ''
    initPromiseReusable = false
    unregisterCurrentDownloadContext()
    currentVersion.value = ''
    lastKnownVersion.value = ''
    projectFiles.value = []
    modelLoadFiles.value = []
    actionRecords.value = {}
    currentRepositoryId.value = null
    currentBranchId.value = null
    currentCommitId.value = ''
    openProjectSnapshotCommitId.value = ''
    writeBaseCommitId.value = ''
    requiresReload.value = false
    pendingInsertedNodeValidations.value = []
    openProjectRootNode.value = null
    openProjectArchiveFile.value = null
    openProjectRootDisplayContext.value = null
    isLoading.value = false
    error.value = null
  }

  /**
   * 应用当前 OpenProject 根节点提交成功后的新 commit。
   *
   * 设计说明：零件包提交会生成新的根仓库提交，但不会重新执行 OpenProject。
   * 因此这里集中同步会话中的 commit 字段，避免页面层分别修改
   * `currentCommitId`、`currentVersion`、`lastKnownVersion` 与根节点快照导致状态不一致。
   *
   * @param commitId 后端返回的新提交 ID。
   */
  function applyOpenProjectRootCommit(commitId: string): void {
    const normalizedCommitId = commitId.trim()
    if (!normalizedCommitId) {
      throw new Error('根节点提交 ID 不能为空')
    }

    currentCommitId.value = normalizedCommitId
    currentVersion.value = normalizedCommitId
    lastKnownVersion.value = normalizedCommitId
    if (openProjectRootNode.value) {
      openProjectRootNode.value.commitId = normalizedCommitId
    }
  }

  /**
   * 应用基于当前 OpenProject 打开快照派生出的写入基线提交。
   *
   * 设计说明：插入零部件会创建新的根仓库提交，但准备阶段不能刷新或改写
   * `openProjectRootNode` 中的打开快照，否则旧调用点会误以为整棵 OpenProject 树已同步。
   *
   * @param commitId 后端创建出的写入基线提交 ID。
   * @throws 当提交 ID 为空白字符串时抛出可定位错误。
   */
  function applyOpenProjectWriteBaseCommit(commitId: string): void {
    const normalizedCommitId = commitId.trim()
    if (!normalizedCommitId) {
      throw new Error('写入基线提交 ID 不能为空')
    }

    currentCommitId.value = normalizedCommitId
    writeBaseCommitId.value = normalizedCommitId
    currentVersion.value = normalizedCommitId
    lastKnownVersion.value = normalizedCommitId
  }

  /**
   * 记录一次插入零部件后的待确认节点校验。
   *
   * @param record 插入操作创建出的父子节点最小校验记录。
   */
  function recordPendingInsertedNodeValidation(
    record: PendingInsertedNodeValidationRecord,
  ): void {
    // 使用新数组替换，确保 Pinia/Vue 能稳定感知队列变化。
    pendingInsertedNodeValidations.value = [
      ...pendingInsertedNodeValidations.value,
      { ...record },
    ]
  }

  /**
   * 清空插入零部件后的待确认节点校验队列。
   */
  function clearPendingInsertedNodeValidations(): void {
    pendingInsertedNodeValidations.value = []
  }

  /**
   * 标记当前 OpenProject 会话需要重新加载。
   *
   * @param reason 触发重新加载的可展示原因。
   */
  function markRequiresReload(reason: string): void {
    requiresReload.value = true
    error.value = reason
  }

  async function recoverSessionOnForeground(
    projectId: OpenProjectSessionSource,
  ): Promise<{
    reconnected: boolean
    versionChanged: boolean
    previousVersion: string
    currentVersion: string
  }> {
    const previousVersion = lastKnownVersion.value
    const projectIdText = typeof projectId === 'string' ? projectId : projectId.projectIdText
    if (!projectIdText.trim()) {
      return {
        reconnected: false,
        versionChanged: false,
        previousVersion,
        currentVersion: previousVersion,
      }
    }
    if (isLoading.value || initPromise) {
      return {
        reconnected: false,
        versionChanged: false,
        previousVersion,
        currentVersion: currentVersion.value,
      }
    }
    if (typeof document !== 'undefined' && document.hidden) {
      return {
        reconnected: false,
        versionChanged: false,
        previousVersion,
        currentVersion: currentVersion.value,
      }
    }

    await initSession(projectId)
    const nextVersion = lastKnownVersion.value
    return {
      reconnected: true,
      versionChanged: isVersionChanged(previousVersion, nextVersion),
      previousVersion,
      currentVersion: nextVersion,
    }
  }

  return {
    sessionId,
    currentVersion,
    lastKnownVersion,
    projectFiles,
    modelLoadFiles,
    actionRecords,
    currentRepositoryId,
    currentBranchId,
    currentCommitId,
    openProjectSnapshotCommitId,
    writeBaseCommitId,
    requiresReload,
    pendingInsertedNodeValidations,
    openProjectRootNode,
    openProjectArchiveFile,
    openProjectRootDisplayContext,
    isLoading,
    error,
    initSession,
    recoverSessionOnForeground,
    isVersionChanged,
    getGlbFiles,
    getPrimaryGlbFile,
    applyOpenProjectRootCommit,
    applyOpenProjectWriteBaseCommit,
    recordPendingInsertedNodeValidation,
    clearPendingInsertedNodeValidations,
    markRequiresReload,
    reset,
  }
})
